import React, { useEffect, useState } from "react";
import { Loader2, LayoutTemplate, Wand2, ChevronDown, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { Panel } from "@/components/common/Panel";
import { SkeletonRows } from "@/components/common/Skeleton";
import { TID } from "@/lib/testIds";
import { toast } from "sonner";

export function RoleTemplatesPanel({ onGenerated }) {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [category, setCategory] = useState("");
    const [expanded, setExpanded] = useState(null);
    const [generating, setGenerating] = useState(null);

    const load = async (cat) => {
        setLoading(true);
        try {
            const { data } = await api.get("/roles/templates", { params: cat ? { category: cat } : {} });
            setTemplates(data.templates);
        } catch {
            setTemplates([]);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(category); }, [category]);

    const generate = async (tmpl) => {
        setGenerating(tmpl.template_id);
        try {
            const { data } = await api.post(`/roles/templates/${tmpl.template_id}/generate`);
            toast.success(`Generated ${data.name} (${data.sod_conflicts.length} SoD conflict(s))`);
            onGenerated?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Template generation failed");
        } finally {
            setGenerating(null);
        }
    };

    return (
        <Panel
            eyebrow="Data-driven · Fiori Apps Library + real GRC ruleset"
            title={`SAP Module Role Templates (${templates.length})`}
            action={
                <select
                    data-testid={TID.roleTemplateCategoryFilter}
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="bg-[var(--bg-base)] border border-white/10 rounded-sm px-2 py-1.5 text-xs font-mono text-slate-200 focus:border-cyan-500/40 focus:outline-none"
                >
                    <option value="">All categories</option>
                    <option value="module">SAP Module</option>
                    <option value="business_process">Business Process (P2P / O2C)</option>
                </select>
            }
        >
            <p className="text-xs text-slate-500 font-mono mb-3">
                Each template&rsquo;s T-code list is compiled from the real, most-referenced transactions in the
                offline Fiori Apps Library per module (or the real GRC ruleset function library for P2P/O2C
                composites) — not hand-picked guesses. Click &ldquo;Generate Role&rdquo; to instantiate + run
                Secure-by-Design SoD analysis instantly.
            </p>
            {loading ? (
                <SkeletonRows rows={6} cols={3} />
            ) : (
                <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
                    {templates.map((t) => {
                        const isOpen = expanded === t.template_id;
                        return (
                            <div key={t.template_id} data-testid={TID.roleTemplateCard(t.template_id)} className="panel p-3.5 rounded-[4px] flex flex-col">
                                <div className="flex items-center gap-2">
                                    <LayoutTemplate size={14} className="text-cyan-400 shrink-0" />
                                    <span className="text-sm text-white font-medium truncate flex-1">{t.module_name}</span>
                                    <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded-sm border shrink-0 ${t.category === "business_process" ? "bg-rose-500/15 text-rose-300 border-rose-500/30" : "bg-cyan-500/10 text-cyan-300 border-cyan-500/25"}`}>
                                        {t.module_code}
                                    </span>
                                </div>
                                <p className="text-[11px] text-slate-500 mt-1.5 leading-relaxed flex-1">{t.description}</p>
                                <button
                                    onClick={() => setExpanded(isOpen ? null : t.template_id)}
                                    className="text-[10px] font-mono text-slate-500 hover:text-cyan-400 inline-flex items-center gap-1 mt-2 transition-colors"
                                >
                                    {isOpen ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
                                    {t.tcode_count} T-codes
                                </button>
                                {isOpen && (
                                    <div className="mt-1.5 text-[10px] font-mono text-slate-400 max-h-[100px] overflow-y-auto border-t border-white/5 pt-1.5">
                                        {t.tcodes.join(", ")}
                                    </div>
                                )}
                                <button
                                    data-testid={TID.roleTemplateGenerateBtn(t.template_id)}
                                    onClick={() => generate(t)}
                                    disabled={generating === t.template_id}
                                    className="btn-primary sheen inline-flex items-center justify-center gap-1.5 mt-3 py-1.5 text-xs disabled:opacity-60"
                                >
                                    {generating === t.template_id ? <Loader2 size={12} className="animate-spin" /> : <Wand2 size={12} />}
                                    Generate Role
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}
        </Panel>
    );
}
